import { monthlyPaymentEur } from '@/lib/financingEstimate';
import { useFxRates } from '@/lib/fxRates';
import { canDirectContact } from '@/utils/dealerContactGate';
import { trackContactClick } from '@/utils/contactTracking';
import React, { useEffect, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Heart, 
  Phone, 
  ArrowsLeftRight, 
  ShieldCheck, 
  FileText, 
  Medal, 
  Calendar, 
  Gauge, 
  GasPump, 
  GearSix,
  MapPin 
} from "@phosphor-icons/react";
import { AspectAwareImage } from "@/components/ui/AspectAwareImage";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder";
import { toast } from "sonner";
import { trackImagePerformance } from "@/utils/performance";
import { ga4SelectItem } from "@/utils/analytics";
import { useViewTracking } from "@/hooks/useAnalytics";
import { VehicleTypeIcon } from "@/components/shared/VehicleTypeIcon";
import { getVehicleLabel } from "@/utils/phosphorIcons";
import { FavoriteButton } from "@/components/listings/card/FavoriteButton";
import { useListingBadges } from "@/hooks/useListingBadges";
import { useCompare } from "@/contexts/CompareContext";
import { supabase } from "@/integrations/supabase/client";
import { useCurrencyConversion } from "@/hooks/useCurrencyConversion";
import { useCountry } from "@/hooks/useCountry";
import { convertDistance } from "@/utils/countryConfig";
import { useTranslationV2 } from "@/hooks/useTranslationV2";
import { localizeTransmission, localizeFuel } from "@/utils/listingLocalization";
import { CarVerticalButton } from "@/components/listings/detail/CarVerticalButton";
import { selectBestCardImage, hasValidPhoto } from "@/utils/imageSelection";
import { generateThumbnailAlt } from "@/utils/imageAltGenerator";
import { getFinancingEligibility } from "@/components/listings/detail/FinancingCTA";
import { WhatsAppCardButton } from "@/components/listings/detail/WhatsAppCardButton";
import { buildListingSlug } from "@/utils/listingSlug";
import { PremiumDealerBadge } from "@/components/dealers/PremiumDealerBadge";
import { formatMakeDisplay } from "@/utils/makeDisplay";



interface ModernListingCardProps {
  id?: string;
  title: string;
  price: number;
  currency?: string;
  monthly?: number;
  photo?: string;
  year: number;
  mileageKm: number;
  fuel_type?: string;
  transmission?: string;
  location: string;
  location_city?: string;
  location_region?: string;
  location_country?: string;
  slug: string;
  make?: string;
  model?: string;
  promoted?: boolean;
  auto_promoted?: boolean;
  is_new_listing?: boolean;
  status?: 'active' | 'sold' | 'inactive';
  vehicle_type?: string;
  verified_dealer?: boolean;
  vin_available?: boolean;
  certified_vehicle?: boolean;
  mileage?: number;
  engine_power?: number;
  images?: string[];
  created_at?: string;
  // Boat specific fields
  boat_type?: string;
  length_mm?: number;
  hull_material?: string;
  operating_hours?: number;
  // Dealer info for financing banner
  user_id?: string;
}

export function ModernListingCard({
  id,
  title,
  price,
  currency = 'EUR',
  monthly,
  photo,
  year,
  mileageKm,
  fuel_type,
  transmission,
  location,
  location_city,
  location_region,
  location_country,
  slug,
  make,
  model,
  promoted = false,
  auto_promoted = false,
  is_new_listing = false,
  status = 'active',
  vehicle_type = 'car',
  verified_dealer = false,
  vin_available = false,
  certified_vehicle = false,
  mileage,
  engine_power,
  images = [],
  created_at,
  boat_type,
  length_mm,
  hull_material,
  operating_hours,
  user_id,
}: ModernListingCardProps) {
  const loadStartTime = performance.now();
  const { trackView } = useViewTracking();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();
  const { formatPrice: formatPriceWithCurrency } = useCurrencyConversion();
  const { data: fxRates } = useFxRates();
  const { config, country } = useCountry();
  const { t: tVehicles } = useTranslationV2('vehicles');
  const { t: tBadges } = useTranslationV2('badges');

  const inCompare = id ? isInCompare(id) : false;

  // Track view when card is rendered
  useEffect(() => {
    if (id) trackView(id);
  }, [id, trackView]);

  const handleClick = () => {
    ga4SelectItem({
      id: id || slug,
      title,
      make,
      model,
      year,
      price
    });
    // Save return anchor and current path for precise restoration on Back
    try {
      sessionStorage.setItem('returnToPath', window.location.pathname + window.location.search);
      sessionStorage.setItem('returnToId', id || slug);
      const key = `scroll:${window.location.pathname}`;
      sessionStorage.setItem(key, String(window.scrollY));
    } catch {}
  };

  const handleCompareToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;
    
    if (inCompare) {
      removeFromCompare(id);
    } else {
      addToCompare({
        id,
        slug,
        make: make || '',
        model: model || '',
        year,
        price,
        mileage: mileage || mileageKm,
        fuel_type: fuel_type || '',
        transmission: transmission || '',
        engine_power,
        images: images.length > 0 ? images : photo ? [photo] : [],
        vehicle_type: vehicle_type || 'car',
      });
    }
  };

  const formatPrice = (value: number) => formatPriceWithCurrency(value, currency as any);
  
  const displayMileage = convertDistance(mileageKm, 'km', config.distanceUnit);
  const formatKm = (value: number) => 
    new Intl.NumberFormat(config.locale).format(value) + " " + config.distanceUnit;

  // Calculate display badges using the hook
  const displayBadges = useListingBadges({
    createdAt: created_at || new Date().toISOString(),
    viewCount: 0,
    isVerified: verified_dealer,
    autoPromoted: auto_promoted,
    autoPromotionEnd: created_at ? new Date(new Date(created_at).getTime() + 48 * 60 * 60 * 1000).toISOString() : undefined,
    certifiedVehicle: certified_vehicle,
    premiumPromoted: promoted,
  });


  const handleContact = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!id) return;
    
    if (!(await canDirectContact(user_id))) { toast.info("Trimite o cerere din pagina anunțului, dealerul te va contacta."); return; }
    try {
      const { data: contactInfo } = await supabase
        .rpc('get_public_contact_info', { _listing_id: id });
      
      if (contactInfo && contactInfo.length > 0) {
        const phone = contactInfo[0].contact_phone;
        if (phone) {
          trackContactClick({ id, sellerId: user_id }, 'phone', 'list_card');
          window.location.href = `tel:${phone}`;
        } else {
          toast.error("Număr de telefon indisponibil");
        }
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast.error("Eroare la contactare");
    }
  };

  // Smart image selection: avoids interior/dashboard/detail shots
  const safePhoto = selectBestCardImage(images, photo);
  const hasValidImage = hasValidPhoto(safePhoto);
  
  // SEO-optimized alt text for Google Images
  const imageAlt = useMemo(() => generateThumbnailAlt({
    make: make || '',
    model: model || '',
    year,
    price,
    currency,
    location_city
  }), [make, model, year, price, currency, location_city]);
  


  // SEO: build descriptive slug from listing fields. Falls back to bare UUID if make/model missing.
  const seoSlug = id ? buildListingSlug({ id, make, model, year, location_city }) : '';
  const detailHref = id ? `/listing/${seoSlug || id}` : `/cars/${slug}`;

  return (
    <Link 
      to={detailHref}
      aria-label={title}
      className="group relative rounded border border-border overflow-hidden bg-card hover:border-primary/40 transition-colors duration-200 block"
      onClick={handleClick}
      data-listing-id={(id || slug)}
    >
      {/* Image Section - 16:9 fixed */}
      <div className="relative aspect-video overflow-hidden bg-muted">
        {hasValidImage ? (
          <div className="w-full h-full">
            <AspectAwareImage
              src={safePhoto!}
              alt={imageAlt}
              width={400}
              height={225}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              onLoad={() => trackImagePerformance(photo || '', loadStartTime)}
            />
          </div>
        ) : (
          <div className="w-full h-full">
            <ImagePlaceholder iconSize="lg" />
          </div>
        )}

        {/* Bottom gradient for badge/price contrast */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />

        {/* Badges - Top Left: stacked verified + status */}
        <div className="absolute top-2 left-2 z-10 flex flex-col gap-1.5 items-start max-w-[60%]">
          
          {displayBadges.slice(0, 2).map((b) => {
            const lbl = b.label.toLowerCase();
            const isPromoted = lbl.includes('premium') || lbl.includes('promov');
            const isNew = lbl.includes('nou') || lbl.includes('new');
            const isCertified = lbl.includes('certificat') || lbl.includes('certified');
            return (
              <Badge
                key={b.label}
                variant="secondary"
                className={`text-[10px] px-2 py-0.5 font-bold shadow-md border-0 backdrop-blur-sm ${
                  isPromoted
                    ? 'bg-accent text-accent-foreground'
                    : isNew
                    ? 'bg-brand-green text-brand-green-foreground'
                    : isCertified
                    ? 'bg-brand-gold text-primary'
                    : 'bg-foreground/90 text-background'
                }`}
              >
                {isNew ? 'Anunț nou' : b.label}
              </Badge>
            );
          })}
        </div>

        {/* Top right - favorite + compare icons */}
        <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5">
          {id && (
            <>
              <button
                type="button"
                onClick={handleCompareToggle}
                className={`p-2 rounded border shadow-sm transition-colors inline-flex items-center gap-1 ${
                  inCompare
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-foreground border-border hover:bg-muted'
                }`}
                title={inCompare ? `Elimină ${title} din comparare` : `Adaugă ${title} la comparare`}
                aria-label={inCompare ? `Elimină ${title} din comparare` : `Adaugă ${title} la comparare`}
                aria-pressed={inCompare}
              >
                <ArrowsLeftRight className="h-4 w-4" weight={inCompare ? "fill" : "bold"} aria-hidden="true" /><span className="text-xs font-semibold">Compară</span>
              </button>
              <div onClick={(e) => e.preventDefault()}>
                <FavoriteButton
                  listingId={id}
                  title={title}
                  size="sm"
                  variant="outline"
                  price={price}
                />
              </div>
            </>
          )}
        </div>

        {/* Sold banner overlay */}
        {status === 'sold' && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="bg-accent-red text-white px-4 py-2 rounded-lg font-bold text-lg shadow-lg transform rotate-12">
              VÂNDUT
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4 space-y-3">
        {/* Title (prominent) & Price (firm but not aggressive) */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-bold text-base md:text-lg leading-tight line-clamp-2 flex-1 text-foreground">{title.length > 40 && make && model ? `${formatMakeDisplay(make)} ${model} ${year}` : title}</h3>
          <div className="text-right shrink-0">
            <div className="font-serif text-xl md:text-[1.375rem] leading-none text-primary whitespace-nowrap tracking-tight font-semibold">
              {formatPrice(price)}
            </div>
            {(() => {
              const ronRate = fxRates?.RON;
              if (!ronRate) return null;
              const priceEur = (currency || 'EUR').toUpperCase() === 'RON' ? price / ronRate : price;
              const eligibility = getFinancingEligibility(year, undefined, priceEur);
              if (!eligibility) return null;
              const priceRon = Math.round(priceEur * ronRate);
              const rataEur = Math.round(monthlyPaymentEur(priceEur));
              const fullTitle = year ? `${title} ${year}` : title;
              const fparams = new URLSearchParams({ type: eligibility, amount: String(Math.round(priceEur)), make: make || '', model: model || '', year: String(year || ''), listing_id: id || slug || '', brand: make || '', marca: make || '', an: String(year || ''), price: String(priceRon), pret: String(priceRon), currency: 'RON', fuel: fuel_type || '', combustibil: fuel_type || '', mileage: String(mileage || mileageKm || ''), km: String(mileage || mileageKm || ''), listingId: id || slug || '', product: eligibility, title: fullTitle, car: fullTitle, vehicul: fullTitle, masina: fullTitle, descriere: fullTitle, utm_source: 'roautomarket', utm_medium: 'card', utm_campaign: `financing_badge_${eligibility}` });
              return (
                <div className="mt-1.5 flex flex-col items-end gap-0.5">
                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`https://www.autocompare.ro/aplica-finantare?${fparams.toString()}`, '_blank', 'noopener,noreferrer'); }} className="inline-flex items-center text-primary text-xs font-semibold whitespace-nowrap hover:underline">
                    {eligibility === 'leasing' ? 'Leasing' : 'Credit'} de la {rataEur} €/lună
                  </button>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Meta chips - cleaner rhythm, colored icons */}
        <div className="flex flex-wrap gap-1.5">
          {vehicle_type === 'boat' ? (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-secondary/60 text-xs font-medium text-foreground">
                <Calendar className="h-3.5 w-3.5 text-primary" weight="duotone" /> {year}
              </span>
              {length_mm && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-secondary/60 text-xs font-medium text-foreground">
                  {(length_mm / 1000).toFixed(1)}m
                </span>
              )}
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-secondary/60 text-xs font-medium text-foreground">
                <Calendar className="h-3.5 w-3.5 text-primary" weight="duotone" /> {year}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-secondary/60 text-xs font-medium text-foreground">
                <Gauge className="h-3.5 w-3.5 text-primary" weight="duotone" /> {formatKm(displayMileage)}
              </span>
              {fuel_type && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-secondary/60 text-xs font-medium text-foreground">
                  <GasPump size={14} className="text-primary" weight="duotone" /> {localizeFuel(fuel_type, config.locale === 'de-DE' ? 'de' : config.locale === 'en-GB' ? 'en-GB' : 'ro')}
                </span>
              )}
              {transmission && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-secondary/60 text-xs font-medium text-foreground">
                  <GearSix size={14} className="text-primary" weight="duotone" />
                  {localizeTransmission(transmission, config.locale === 'de-DE' ? 'de' : config.locale === 'en-GB' ? 'en-GB' : 'ro')}
                </span>
              )}
            </>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 text-primary/70" weight="fill" />
          <span>
            {[
              location_city, 
              location_region, 
              location_country === 'RO' ? 'România' : 
              location_country === 'UK' ? 'Marea Britanie' : 
              location_country === 'DE' ? 'Germania' : 
              location_country
            ].filter(Boolean).join(', ') || location}
          </span>
        </div>

        {/* CTAs */}
        {id && (
          <div className="flex gap-2 min-w-0">
            <Button
              onClick={handleContact}
              size="sm"
              className="bronze-action flex-1 min-w-0 rounded text-xs sm:text-sm font-semibold gap-1 px-2 whitespace-nowrap"
              variant="default"
            >
              <Phone className="w-4 h-4 flex-shrink-0" weight="fill" />
              <span className="truncate">{country === 'UK' ? 'Call' : country === 'DE' ? 'Anrufen' : 'Sună'}</span>
            </Button>
            <WhatsAppCardButton
              listingId={id}
              listingTitle={title}
              sellerId={user_id}
              size="sm"
              className="flex-1 min-w-0 rounded-xl px-2 whitespace-nowrap"
            />
          </div>
        )}
      </div>
    </Link>
  );
}

export default ModernListingCard;
